/**
 * Fees with Refund Table Component
 * Displays fee details with refund amounts and adjusted totals
 */
import React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import LoadingComponent from '../../loadingComponent';

/**
 * Fees with Refund Details Table Component
 * @param {Object} studentData - Student data
 * @param {Object} feeHistoryData - Fee history data from API (contains refundHistory array)
 * @param {Boolean} isLoading - Loading state
 * @returns {JSX.Element} - Fees with Refund table component
 */
export const FeesWithRefundTable = ({ studentData, feeHistoryData = null, isLoading = false }) => {
    const feesWithRefundData = feeHistoryData?.refundHistory || [];

    const totals = feesWithRefundData.reduce((acc, item) => ({
        feeAmount: acc.feeAmount + item.feeAmount,
        concession: acc.concession + item.concession,
        netAmount: acc.netAmount + item.netAmount,
        paidAmount: acc.paidAmount + item.paidAmount,
        balance: acc.balance + item.balance,
        adjustedRefundAmount: acc.adjustedRefundAmount + item.adjustedRefundAmount,
        totalPaidAfterRefund: acc.paidAmount + item.paidAmount
    }), {
        feeAmount: 0,
        concession: 0,
        netAmount: 0,
        paidAmount: 0,
        balance: 0,
        adjustedRefundAmount: 0,
        totalPaidAfterRefund: 0
    });

    const amountBodyTemplate = (field) => (rowData) => {
        const value = rowData[field];
        
        return (
            <span style={{
                fontFamily: 'InterMedium',
                color: '#495057',
                fontSize: '0.9375rem'
            }}>
                {value != null ? value.toFixed(2) : '0.00'}
            </span>
        );
    };

    if (isLoading) {
        return <LoadingComponent />;
    }

    if (!feesWithRefundData || feesWithRefundData.length === 0) {
        return (
            <div className="tab-content feetype-wise-content">
                <div className="fee-table-container card datatable-crud-demo" style={{ overflow: 'auto' }}>
                    <DataTable
                        value={[]}
                        className="fee-details-datatable"
                        showGridlines
                        stripedRows
                        emptyMessage={
                            <div style={{
                                textAlign: 'center',
                                padding: '2rem',
                                color: '#6B7280',
                                fontSize: '0.875rem',
                                fontFamily: 'InterRegular'
                            }}>
                                No refund history available
                            </div>
                        }
                    >
                        <Column
                            field="feeType"
                            header="Feetype"
                            style={{ width: '150px', minWidth: '150px' }}
                            headerStyle={{ width: '150px', minWidth: '150px' }}
                        />
                        <Column
                            field="feeAmount"
                            header="Fee Amount"
                            style={{ width: '120px', minWidth: '120px' }}
                            headerStyle={{ width: '120px', minWidth: '120px' }}
                        />
                        <Column
                            field="concession"
                            header="Concession"
                            style={{ width: '120px', minWidth: '120px' }}
                            headerStyle={{ width: '120px', minWidth: '120px' }}
                        />
                        <Column
                            field="netAmount"
                            header="Net Amount"
                            style={{ width: '120px', minWidth: '120px' }}
                            headerStyle={{ width: '120px', minWidth: '120px' }}
                        />
                        <Column
                            field="paidAmount"
                            header="Paid Amount"
                            style={{ width: '120px', minWidth: '120px' }}
                            headerStyle={{ width: '120px', minWidth: '120px' }}
                        />
                        <Column
                            field="balance"
                            header="Balance"
                            style={{ width: '120px', minWidth: '120px' }}
                            headerStyle={{ width: '120px', minWidth: '120px' }}
                        />
                        <Column
                            field="adjustedRefundAmount"
                            header="Refund Amount"
                            style={{ width: '180px', minWidth: '180px' }}
                            headerStyle={{ width: '180px', minWidth: '180px' }}
                        />
                        <Column
                            field="paidAmount"
                            header="Total Paid"
                            style={{ width: '230px', minWidth: '230px' }}
                            headerStyle={{ width: '230px', minWidth: '230px' }}
                        />
                    </DataTable>
                </div>
            </div>
        );
    }

    const groupedData = {};
    feesWithRefundData.forEach(item => {
        const className = item.className || 'Unknown Class';
        if (!groupedData[className]) {
            groupedData[className] = [];
        }
        groupedData[className].push(item);
    });

    return (
        <div className="tab-content feetype-wise-content">
            {Object.entries(groupedData).map(([className, records]) => (
                <div key={className} style={{ marginBottom: '2rem' }}>
                    <div className="fee-table-container card datatable-crud-demo" style={{ overflow: 'auto' }}>
                        <DataTable
                            value={records}
                            className="fee-details-datatable"
                            showGridlines
                            stripedRows
                        >
                            <Column
                                field="feeType"
                                header="Feetype"
                                style={{ width: '150px', minWidth: '150px' }}
                                headerStyle={{ width: '150px', minWidth: '150px' }}
                            />
                            <Column
                                field="feeAmount"
                                header="Fee Amount"
                                body={amountBodyTemplate('feeAmount')}
                                style={{ width: '120px', minWidth: '120px' }}
                                headerStyle={{ width: '120px', minWidth: '120px' }}
                            />
                            <Column
                                field="concession"
                                header="Concession"
                                body={amountBodyTemplate('concession')}
                                style={{ width: '120px', minWidth: '120px' }}
                                headerStyle={{ width: '120px', minWidth: '120px' }}
                            />
                            <Column
                                field="netAmount"
                                header="Net Amount"
                                body={amountBodyTemplate('netAmount')}
                                style={{ width: '120px', minWidth: '120px' }}
                                headerStyle={{ width: '120px', minWidth: '120px' }}
                            />
                            <Column
                                field="paidAmount"
                                header="Paid Amount"
                                body={amountBodyTemplate('paidAmount')}
                                style={{ width: '120px', minWidth: '120px' }}
                                headerStyle={{ width: '120px', minWidth: '120px' }}
                            />
                            <Column
                                field="balance"
                                header="Balance"
                                body={amountBodyTemplate('balance')}
                                style={{ width: '120px', minWidth: '120px' }}
                                headerStyle={{ width: '120px', minWidth: '120px' }}
                            />
                            <Column
                                field="adjustedRefundAmount"
                                header="Refund Amount"
                                body={amountBodyTemplate('adjustedRefundAmount')}
                                style={{ width: '180px', minWidth: '180px' }}
                                headerStyle={{ width: '180px', minWidth: '180px' }}
                            />
                            <Column
                                field="paidAmount"
                                header="Total Paid"
                                body={amountBodyTemplate('paidAmount')}
                                style={{ width: '230px', minWidth: '230px' }}
                                headerStyle={{ width: '230px', minWidth: '230px' }}
                            />
                        </DataTable>

                        <DataTable
                            value={[{
                                sNo: '',
                                feeType: `Total`,
                                feePlan: '',
                                ...totals
                            }]}
                            className="fee-totals-table"
                            showGridlines
                        >
                            <Column
                                body={(rowData) => <strong style={{ color: '#076EFF', fontFamily: 'InterSemiBold' }}>{rowData.feeType}</strong>}
                                style={{ width: '150px', minWidth: '150px', backgroundColor: '#F3F4F6' }}
                                headerStyle={{ width: '150px', minWidth: '150px' }}
                            />
                            <Column
                                body={(rowData) => <strong style={{ color: '#076EFF', fontFamily: 'InterSemiBold' }}>{rowData.feeAmount.toFixed(2)}</strong>}
                                style={{ width: '120px', minWidth: '120px', backgroundColor: '#F3F4F6' }}
                                headerStyle={{ width: '120px', minWidth: '120px' }}
                            />
                            <Column
                                body={(rowData) => <strong style={{ color: '#076EFF', fontFamily: 'InterSemiBold' }}>{rowData.concession.toFixed(2)}</strong>}
                                style={{ width: '120px', minWidth: '120px', backgroundColor: '#F3F4F6' }}
                                headerStyle={{ width: '120px', minWidth: '120px' }}
                            />
                            <Column
                                body={(rowData) => <strong style={{ color: '#076EFF', fontFamily: 'InterSemiBold' }}>{rowData.netAmount.toFixed(2)}</strong>}
                                style={{ width: '120px', minWidth: '120px', backgroundColor: '#F3F4F6' }}
                                headerStyle={{ width: '120px', minWidth: '120px' }}
                            />
                            <Column
                                body={(rowData) => <strong style={{ color: '#076EFF', fontFamily: 'InterSemiBold' }}>{rowData.paidAmount.toFixed(2)}</strong>}
                                style={{ width: '120px', minWidth: '120px', backgroundColor: '#F3F4F6' }}
                                headerStyle={{ width: '120px', minWidth: '120px' }}
                            />
                            <Column
                                body={(rowData) => <strong style={{ color: '#076EFF', fontFamily: 'InterSemiBold' }}>{rowData.balance.toFixed(2)}</strong>}
                                style={{ width: '120px', minWidth: '120px', backgroundColor: '#F3F4F6' }}
                                headerStyle={{ width: '120px', minWidth: '120px' }}
                            />
                            <Column
                                body={(rowData) => <strong style={{ color: '#076EFF', fontFamily: 'InterSemiBold' }}>{rowData.adjustedRefundAmount.toFixed(2)}</strong>}
                                style={{ width: '180px', minWidth: '180px', backgroundColor: '#F3F4F6' }}
                                headerStyle={{ width: '180px', minWidth: '180px' }}
                            />
                            <Column
                                body={(rowData) => <strong style={{ color: '#076EFF', fontFamily: 'InterSemiBold' }}>{rowData.totalPaidAfterRefund.toFixed(2)}</strong>}
                                style={{ width: '180px', minWidth: '180px', backgroundColor: '#F3F4F6' }}
                                headerStyle={{ width: '180px', minWidth: '180px' }}
                            />
                        </DataTable>
                    </div>
                </div>
            ))}
        </div>
    );
};
